import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

function memberLabel(m) {
  if (!m) return '—';
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${m.wellis_id || '—'})`;
}

export default function SalesReports() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin');

  const [salesTeam, setSalesTeam] = useState([]);
  const [salesTeamTotals, setSalesTeamTotals] = useState(null);
  const [userStatusCounts, setUserStatusCounts] = useState(null);
  const [orderStatusCounts, setOrderStatusCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [authLoading, user, isAdmin, navigate]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/sales-reports`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load sales reports');
      setSalesTeam(data.salesTeam || []);
      setSalesTeamTotals(data.salesTeamTotals || null);
      setUserStatusCounts(data.userStatusCounts || null);
      setOrderStatusCounts(data.orderStatusCounts || null);
    } catch (err) {
      setError(err.message || 'Failed to load sales reports');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    fetchReport();
  }, [user, isAdmin, fetchReport]);

  if (!user || !isAdmin) return null;

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const totalUsers = userStatusCounts ? userStatusCounts.approved + userStatusCounts.verified : 0;
  const totalOrders = orderStatusCounts
    ? orderStatusCounts.pending + orderStatusCounts.confirmed + orderStatusCounts.rejected + orderStatusCounts.cancelled
    : 0;

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-headline-lg font-bold mb-6" style={{ color: '#c9a961' }}>
          {tt('Sales Reports', '销售报告')}
        </h1>

        {error && (
          <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
            {error}
          </div>
        )}

        {loading ? (
          <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
        ) : (
          <>
            {/* Section 1: Sales Team credit processes */}
            <div className="border rounded-lg p-6 mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <h3 className="text-headline-sm font-bold mb-1" style={{ color: '#c9a961' }}>
                {tt('Sales Team — Credit Processes', '销售团队 — 认购积分处理')}
              </h3>
              <p className="text-body-xs mb-4" style={{ color: '#999999' }}>
                {tt(
                  'Signups funded by each sales-team member via /signup-internal, and how much of that spend has been remitted back.',
                  '每位销售团队成员通过 /signup-internal 资助的注册，以及已汇回公司的金额。'
                )}
              </p>

              {salesTeamTotals && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                  {[
                    [tt('Members', '成员数'), salesTeam.length, false],
                    [tt('Signups Funded', '资助注册数'), salesTeamTotals.signups_count, false],
                    [tt('Total Credit Spent', '认购积分总支出'), fmt(salesTeamTotals.total_credit_spent), true],
                    [tt('Remitted (Confirmed)', '已汇回（已确认）'), fmt(salesTeamTotals.confirmed_remitted), true],
                    [tt('Outstanding', '未结清'), fmt(salesTeamTotals.outstanding), true],
                  ].map(([label, value]) => (
                    <div key={label} className="p-3 rounded border text-center" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
                      <div className="text-body-xs" style={{ color: '#999999' }}>{label}</div>
                      <div className="font-bold" style={{ color: '#c9a961' }}>{value}</div>
                    </div>
                  ))}
                </div>
              )}

              {salesTeam.length === 0 ? (
                <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('No sales-team members yet.', '暂无销售团队成员。')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                    <thead>
                      <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                        <th className="p-2">{tt('Member', '成员')}</th>
                        <th className="p-2">{tt('Credit Balance', '认购积分余额')}</th>
                        <th className="p-2">{tt('RnR Balance', 'RnR 余额')}</th>
                        <th className="p-2">{tt('Signups', '注册数')}</th>
                        <th className="p-2">{tt('Credit Spent', '积分支出')}</th>
                        <th className="p-2">{tt('Remitted', '已汇回')}</th>
                        <th className="p-2">{tt('Pending', '待审核')}</th>
                        <th className="p-2">{tt('Outstanding', '未结清')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesTeam.map((m) => (
                        <tr key={m.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                          <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(m)}</td>
                          <td className="p-2">{fmt(m.credit_balance)}</td>
                          <td className="p-2">{fmt(m.rnr_point_balance)}</td>
                          <td className="p-2">{m.signups_count}</td>
                          <td className="p-2">{fmt(m.total_credit_spent)}</td>
                          <td className="p-2">{fmt(m.confirmed_remitted)}</td>
                          <td className="p-2">{fmt(m.pending_remitted)}</td>
                          <td className="p-2" style={{ color: m.outstanding > 0 ? '#d97706' : '#b8a68f' }}>{fmt(m.outstanding)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 2: Approved / Verified statuses */}
            <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <h3 className="text-headline-sm font-bold mb-1" style={{ color: '#c9a961' }}>
                {tt('Approved & Verified Statuses', '已批准与已验证状态')}
              </h3>
              <p className="text-body-xs mb-4" style={{ color: '#999999' }}>
                {tt(
                  '"Verified" means the member has an active package (package_id set) -- not a separate approval step. "Approved" is everyone else who signed up but hasn’t activated a package yet.',
                  '"已验证"指该会员已有生效套餐（已设置 package_id）——并非另一道审批步骤。"已批准"指已注册但尚未激活套餐的会员。'
                )}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <h4 className="text-body-sm font-semibold mb-2" style={{ color: '#c9a961' }}>{tt('Members by Status', '会员状态分布')}</h4>
                  <div className="space-y-2">
                    {userStatusCounts && [
                      [tt('Approved (no package yet)', '已批准（尚未激活套餐）'), userStatusCounts.approved],
                      [tt('Verified (package active)', '已验证（套餐生效中）'), userStatusCounts.verified],
                    ].map(([label, value]) => (
                      <div key={label} className="flex items-center justify-between p-2 rounded border" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
                        <span className="text-body-sm">{label}</span>
                        <span className="font-bold" style={{ color: '#c9a961' }}>
                          {value} {totalUsers > 0 && <span className="text-body-xs" style={{ color: '#999999' }}>({((value / totalUsers) * 100).toFixed(1)}%)</span>}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between p-2 rounded border" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
                      <span className="text-body-sm font-semibold">{tt('Total Members', '会员总数')}</span>
                      <span className="font-bold" style={{ color: '#c9a961' }}>{totalUsers}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-body-sm font-semibold mb-2" style={{ color: '#c9a961' }}>{tt('Package Orders by Status', '套餐订单状态分布')}</h4>
                  <div className="space-y-2">
                    {orderStatusCounts && [
                      [tt('Pending', '待处理'), orderStatusCounts.pending],
                      [tt('Confirmed', '已确认'), orderStatusCounts.confirmed],
                      [tt('Rejected', '已拒绝'), orderStatusCounts.rejected],
                      [tt('Cancelled', '已取消'), orderStatusCounts.cancelled],
                    ].map(([label, value]) => (
                      <div key={label} className="flex items-center justify-between p-2 rounded border" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
                        <span className="text-body-sm">{label}</span>
                        <span className="font-bold" style={{ color: '#c9a961' }}>
                          {value} {totalOrders > 0 && <span className="text-body-xs" style={{ color: '#999999' }}>({((value / totalOrders) * 100).toFixed(1)}%)</span>}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between p-2 rounded border" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
                      <span className="text-body-sm font-semibold">{tt('Total Orders', '订单总数')}</span>
                      <span className="font-bold" style={{ color: '#c9a961' }}>{totalOrders}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
